import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { formatDistanceToNow, format } from 'date-fns'
import { getStats, UrlStats } from '../lib/api'
import styles from './Stats.module.css'

export default function Stats() {
  const { code } = useParams<{ code: string }>()
  const [stats, setStats] = useState<UrlStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!code) return
    getStats(code)
      .then(setStats)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [code])

  if (loading) return (
    <div className={styles.center}>
      <div className={styles.spinner} />
    </div>
  )

  if (error || !stats) return (
    <div className={styles.center}>
      <p className={styles.errorMsg}>Link not found.</p>
      <Link className={styles.back} to="/">← Back</Link>
    </div>
  )

  const shortUrl = `${import.meta.env.VITE_API_BASE_URL ?? window.location.origin}/${stats.shortCode}`

  return (
    <div className={styles.page}>
      <div className={styles.blob} />

      <header className={styles.header}>
        <Link className={styles.back} to="/">← Back</Link>
        <h1 className={styles.title}>Link Analytics</h1>
      </header>

      <main className={styles.main}>
        {/* Hero stat card */}
        <motion.div
          className={styles.heroCard}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .4 }}
        >
          <div>
            <span className={styles.metaLabel}>Short link</span>
            <span className={styles.shortUrl}>{shortUrl}</span>
            <span className={styles.originalUrl}>{stats.originalUrl}</span>
          </div>
          <div className={styles.clickBadge}>
            <span className={styles.clickNum}>{stats.clickCount.toLocaleString()}</span>
            <span className={styles.clickLabel}>total clicks</span>
          </div>
        </motion.div>

        {/* Stat grid */}
        <div className={styles.statGrid}>
          {[
            { label: 'Created', value: format(new Date(stats.createdAt), 'MMM d, yyyy') },
            {
              label: 'Expires',
              value: stats.expiresAt
                ? format(new Date(stats.expiresAt), 'MMM d, yyyy')
                : 'Never',
            },
            {
              label: 'Status',
              value: !stats.isActive
                ? 'Inactive'
                : (stats.expiresAt && new Date(stats.expiresAt) < new Date())
                  ? 'Expired'
                  : 'Active',
              accent: stats.isActive && !(stats.expiresAt && new Date(stats.expiresAt) < new Date()),
              danger: !!(stats.expiresAt && new Date(stats.expiresAt) < new Date()),
            },
            { label: 'Short code', value: stats.shortCode, mono: true },
            { label: 'Created by', value: stats.createdBy ?? '—', mono: true },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              className={styles.statCard}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <span className={styles.statLabel}>{s.label}</span>
              <span
                className={`${styles.statValue} ${s.mono ? styles.mono : ''} ${s.accent ? styles.accent : ''} ${'danger' in s && s.danger ? styles.danger : ''}`}
              >
                {s.value}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Recent clicks */}
        {stats.recentClicks.length > 0 && (
          <section className={styles.clickList}>
            <h2 className={styles.sectionTitle}>Recent clicks</h2>
            {stats.recentClicks.map((c, i) => (
              <motion.div
                key={c.id}
                className={styles.clickRow}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <div className={styles.clickMeta}>
                  <span className={styles.clickTime}>
                    {formatDistanceToNow(new Date(c.clickedAt))} ago
                  </span>
                  {c.referrer && (
                    <span className={styles.clickRef} title={c.referrer}>
                      from {new URL(c.referrer).hostname}
                    </span>
                  )}
                </div>
                <span className={styles.clickIp}>{c.ipAddress ?? '—'}</span>
              </motion.div>
            ))}
          </section>
        )}

        {stats.recentClicks.length === 0 && (
          <div className={styles.noClicks}>
            <p>No clicks recorded yet.</p>
          </div>
        )}
      </main>
    </div>
  )
}
